<!-- @format -->

<template>
	<div class="contenedor-tabla">
		<v-data-table
			:headers="cabeceras"
			:items="ordenes"
			:loading="cargando"
			dense
			:value="seleccion"
			item-key="clave"
			show-select
			mobile-breakpoint="0"
			class="tabla-ot elevation-0"
			loading-text="Buscando órdenes de trabajo…"
			:server-items-length="total"
			:options="opciones"
			:item-class="claseFila"
			hide-default-footer
			@input="valor => $emit('seleccionar', valor)"
			@update:options="valor => $emit('opciones', valor)"
		>
			<template v-slot:no-data>
				<data-empty
					title="Sin órdenes"
					msj="No hay órdenes de trabajo para los filtros aplicados"
					:height="120"
				/>
			</template>

			<template v-slot:item.estado="{ item }">
				<v-chip
					x-small
					label
					:color="item.esCertificada ? 'success' : 'warning'"
					text-color="white"
				>
					{{ item.esCertificada ? 'Certificada' : 'Pendiente' }}
				</v-chip>
				<v-icon
					v-if="item.tieneFirma === false"
					small
					color="error"
					class="ml-1"
					title="Sin firma del cliente"
					>mdi-draw-pen</v-icon
				>
			</template>

			<template v-slot:item.folio="{ item }">
				<span class="font-weight-bold">{{ item.folio }}</span>
			</template>

			<template v-slot:item.tipoEtiqueta="{ item }">
				<div class="d-flex align-center">
					<v-icon small :color="item.tipoColor" class="mr-1">{{ item.tipoIcono }}</v-icon>
					<span>{{ item.tipoEtiqueta }}</span>
					<v-chip v-if="item.emergencia" x-small color="error" class="ml-1" outlined>
						Emergencia
					</v-chip>
				</div>
			</template>

			<template v-slot:item.lugar="{ item }">
				<div class="text-truncate" style="max-width: 190px" :title="item.lugar">
					{{ item.lugar }}
				</div>
			</template>

			<template v-slot:item.tecnico="{ item }">
				<div class="text-truncate" style="max-width: 120px" :title="item.tecnico">
					{{ nombreCorto(item.tecnico) }}
				</div>
			</template>

			<template v-slot:item.fechaActividad="{ item }">
				<span :title="`OT registrada el ${fechaHora(item.fechaOT)}`">{{
					fecha(item.fechaActividad)
				}}</span>
			</template>

			<template v-slot:item.fechaOT="{ item }">
				{{ fecha(item.fechaOT) }}
			</template>

			<template v-slot:item.fechaCertificado="{ item }">
				{{ item.fechaCertificado ? fecha(item.fechaCertificado) : '—' }}
			</template>

			<template v-slot:item.acciones="{ item }">
				<work-order-row-actions
					:orden="item"
					:descargando="descargando === item.clave"
					:compartiendo="compartiendo === item.clave"
					@compartir="orden => $emit('compartir', orden)"
					@descargar="orden => $emit('descargar', orden)"
					@copiar="orden => $emit('copiar', orden)"
				/>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import { textoFecha, textoFechaHora } from '@/utils/workOrders/fechas';

export default {
	name: 'WorkOrderTable',
	components: {
		DataEmpty: () => import('@/components/ui/DataEmpty.vue'),
		WorkOrderRowActions: () => import('./WorkOrderRowActions.vue'),
	},
	props: {
		ordenes: { type: Array, default: () => [] },
		seleccion: { type: Array, default: () => [] },
		total: { type: Number, default: 0 },
		opciones: { type: Object, default: () => ({}) },
		cargando: { type: Boolean, default: false },
		descargando: { type: String, default: '' },
		compartiendo: { type: String, default: '' },
	},
	data() {
		return {
			cabeceras: [
				{ text: 'Estado', value: 'estado', sortable: false },
				{ text: 'Folio', value: 'folio', sortable: true },
				{ text: 'Tipo', value: 'tipoEtiqueta', sortable: false },
				{ text: 'Cliente / Lugar', value: 'lugar', sortable: false },
				{ text: 'Técnico', value: 'tecnico', sortable: false },
				{ text: 'Actividad', value: 'fechaActividad', sortable: true },
				{ text: 'OT', value: 'fechaOT', sortable: true },
				{ text: 'Certificado', value: 'fechaCertificado', sortable: true },
				{ text: 'Acciones', value: 'acciones', sortable: false, align: 'end' },
			],
		};
	},
	methods: {
		fecha: valor => textoFecha(valor, 'DD/MM/YY'),
		fechaHora: textoFechaHora,
		nombreCorto(nombre) {
			// nombre + primer apellido: el nombre completo no cabe y quedaba cortado
			return String(nombre || '')
				.split(' ')
				.slice(0, 2)
				.join(' ');
		},
		claseFila(orden) {
			return orden.esCertificada ? 'fila-certificada' : 'fila-pendiente';
		},
	},
};
</script>

<style lang="scss" scoped>
/* Mismo marco que la tabla del gestor de certplag: la caja de meses es su tapa,
   de ahi que el borde superior sea recto y el inferior redondeado. */
.contenedor-tabla {
	max-width: 1500px;
	margin: 0 auto;
	overflow-x: auto;
	background-color: #fff;
	border: 1px solid #cad8dd;
	border-radius: 0 0 10px 10px;
}

.tabla-ot {
	background-color: transparent;

	::v-deep table {
		font-size: 12px;
	}

	::v-deep th {
		/* certplag usa 90px; aca hay una columna mas de acciones y con 70px
		   entra la fila completa sin scroll lateral en 1366px */
		min-width: 70px !important;
		white-space: nowrap;
		font-size: 12px !important;
		color: #37474f !important;
		font-weight: 600;
	}

	::v-deep td {
		white-space: nowrap;
		padding: 0 10px !important;
		height: 38px !important;
	}

	::v-deep tbody tr:nth-child(odd) {
		background-color: #6494b517;
	}

	::v-deep .v-data-table__wrapper {
		overflow-x: visible;
	}
}

/* Estado de la fila, como en certplag */
.tabla-ot ::v-deep tr.fila-certificada {
	background-color: #e8f5e9 !important;
}

.tabla-ot ::v-deep tr.fila-pendiente td:first-child {
	box-shadow: inset 3px 0 0 0 #ffb74d;
}

@media (max-width: 599px) {
	.tabla-ot ::v-deep table {
		font-size: 12.5px;
	}
}
</style>
